package top.wcpe.allincore.ui.runtime.element

import top.wcpe.allincore.layout.engine.LayoutMode
import kotlin.jvm.JvmName
import kotlin.jvm.JvmStatic
import kotlin.math.ceil

/**
 * 多列虚拟列表：Excel 风格固定 N 列、每列宽度独立，行虚拟化；可选固定表头。
 *
 * 列由 [ColumnDef] 定义（key / title / width / align）。表头高 [headerHeight]，[headerSticky]
 * 为 true 时表头不随内容滚动。行虚拟化与 [VirtualList] 一致，但行的「内容坐标系顶部」在表头之下
 * （`headerSticky` 时滚动区从 headerHeight 起算）。
 *
 * 窗口子节点 0 基排布，[contentOffsetY] 叠加表头高度与窗口首项偏移。
 */
@Suppress("TooManyFunctions", "MagicNumber")
class MultiColumnVirtualList private constructor() : ScrollableContainer<MultiColumnVirtualList>() {

    /** 列对齐。 */
    enum class ColumnAlign {
        START,
        CENTER,
        END,
    }

    /** 列定义。 */
    data class ColumnDef(
        @get:JvmName("key") val key: String,
        @get:JvmName("title") val title: String,
        @get:JvmName("width") val width: Float,
        @get:JvmName("align") val align: ColumnAlign,
    )

    private var columnDefsField: List<ColumnDef> = emptyList()
    private var itemHeightField: Float = 18f
    private var headerHeightField: Float = 20f
    private var headerStickyField: Boolean = true
    private var itemCountField: Int = 0
    private var bufferField: Int = 2

    private var windowChildren: List<UIElement<*>> = emptyList()
    private var lastFirst = -1
    private var lastLast = -1
    private var lastItemCount = -1
    private var materializer: WindowMaterializer? = null

    init {
        styleField.layout().mode = LayoutMode.COLUMN
    }

    override fun self(): MultiColumnVirtualList = this

    // -------- 属性 --------

    fun columnDefs(defs: List<ColumnDef>?): MultiColumnVirtualList = apply {
        columnDefsField = defs?.toList() ?: emptyList()
    }

    fun columnDefs(): List<ColumnDef> = columnDefsField

    fun itemHeight(h: Float): MultiColumnVirtualList = apply {
        itemHeightField = h.coerceAtLeast(1f)
        invalidateWindow()
    }

    fun itemHeight(): Float = itemHeightField

    fun headerHeight(h: Float): MultiColumnVirtualList = apply {
        headerHeightField = h.coerceAtLeast(0f)
        scrollY(scrollY())
        invalidateWindow()
    }

    fun headerHeight(): Float = headerHeightField

    fun headerSticky(sticky: Boolean): MultiColumnVirtualList = apply { headerStickyField = sticky }

    fun headerSticky(): Boolean = headerStickyField

    fun itemCount(count: Int): MultiColumnVirtualList = apply {
        itemCountField = count.coerceAtLeast(0)
        scrollY(scrollY())
        invalidateWindow()
    }

    fun itemCount(): Int = itemCountField

    fun buffer(b: Int): MultiColumnVirtualList = apply {
        bufferField = b.coerceAtLeast(0)
        invalidateWindow()
    }

    fun buffer(): Int = bufferField

    /** 所有列宽之和，作为内容横向尺寸。 */
    fun totalColumnsWidth(): Float = columnDefsField.fold(0f) { sum, c -> sum + c.width }

    // -------- 内容尺寸 / 偏移 --------

    override fun requiresContentMeasure(): Boolean = false

    override fun contentHeight(): Float = // 行内容总高（不含 sticky 表头，表头浮在视窗顶部）。
        itemCountField * itemHeightField + stickyHeaderReserve()

    /** sticky 表头在内容高度里占的预留量：sticky 时表头不滚，需让可滚区 = 行总高。 */
    private fun stickyHeaderReserve(): Float = if (headerStickyField) headerHeightField else 0f

    /** 行视窗的「可视高度」= 视窗高 - sticky 表头。 */
    fun rowViewportHeight(): Float = (viewportHeight() - stickyHeaderReserve()).coerceAtLeast(0f)

    override fun contentOffsetY(): Float = // 表头之下起算 + 窗口首项偏移 - 滚动偏移。
        headerHeightField + firstVisible() * itemHeightField - scrollY()

    // -------- 窗口范围 --------

    fun firstVisible(): Int = ((scrollY() / itemHeightField).toInt() - bufferField).coerceAtLeast(0)

    fun lastVisible(): Int {
        val visibleCount = ceil(rowViewportHeight() / itemHeightField).toInt()
        val raw = (scrollY() / itemHeightField).toInt() + visibleCount + bufferField
        return raw.coerceAtMost(itemCountField - 1)
    }

    // -------- 子节点管理 --------

    override fun children(): List<UIElement<*>> = windowChildren

    fun setWindowChildren(children: List<UIElement<*>>?) {
        windowChildren = children ?: emptyList()
        // FR-30 B1：windowChildren 经 children() 返回、绕过会标脏的 child()/replaceChildren()，
        // 滚动实例化新窗口时须显式标布局脏，否则整树布局 memo 命中返回过期布局、新窗口项不被定位（滚动后内容消失 / 堆顶）。
        markLayoutDirty()
    }

    fun materializeWindow(m: WindowMaterializer) {
        materializer = m
    }

    override fun onScrollStateChanged() {
        super.onScrollStateChanged()
        invalidateWindow()
    }

    private fun invalidateWindow() {
        val m = materializer ?: return
        if (itemCountField <= 0) {
            if (windowChildren.isNotEmpty()) {
                lastFirst = -1
                lastLast = -1
                lastItemCount = 0
                m.onWindowChanged(this, 0, -1)
            }
            return
        }
        val first = firstVisible()
        val last = lastVisible()
        if (first == lastFirst && last == lastLast && itemCountField == lastItemCount) {
            return
        }
        lastFirst = first
        lastLast = last
        lastItemCount = itemCountField
        m.onWindowChanged(this, first, last)
    }

    fun interface WindowMaterializer {
        /**
         * 窗口范围变化时调用。
         *
         * @param list  MultiColumnVirtualList 实例
         * @param first 第一个需实例化的行 index（含）
         * @param last  最后一个需实例化的行 index（含）；itemCount=0 时 last < first
         */
        fun onWindowChanged(list: MultiColumnVirtualList, first: Int, last: Int)
    }

    companion object {
        @JvmStatic
        fun create(): MultiColumnVirtualList = MultiColumnVirtualList()
    }
}
